"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConfirmPopup = void 0;
const Popup_1 = require("./Popup");
const pixi_js_1 = require("pixi.js");
const ui_1 = require("@pixi/ui");
const Global_1 = require("../Global");
const i18next_1 = __importDefault(require("i18next"));
const blocks_1 = require("../blocks");
const WigglyText_1 = require("../objects/WigglyText");
const settings_1 = require("../config/settings");
const AssetManager_1 = __importDefault(require("../managers/AssetManager"));
class ConfirmPopup extends Popup_1.Popup {
    constructor(app, text = i18next_1.default.t('confirm'), yes = i18next_1.default.t('yes'), no = null, icon = null, animate = true) {
        super(app, animate);
        this.text = text;
        this.yes = yes;
        this.no = no;
        this.icon = icon;
        this.confirm = null;
        this.cancel = null;
        if (this.text) {
            this.info = this.makeInfo();
        }
        if (this.no) {
            this.noButton = this.makeNoButton();
            this.sigs.push(this.noButton.onPress.connect(() => {
                this.destroy({ children: true });
                if (this.cancel) {
                    this.cancel();
                }
                AssetManager_1.default.tickSound();
            }));
        }
        this.yesButton = this.makeYesButton();
        this.sigs.push(this.yesButton.onPress.connect(() => {
            this.destroy({ children: true });
            if (this.confirm) {
                this.confirm();
            }
            AssetManager_1.default.tickSound();
        }));
        if (this.icon) {
            if (this.icon instanceof pixi_js_1.Sprite) {
                this.icon.anchor.set(0.5, 0);
            }
            this.icon.position.set(0, 0);
            this.background.addChild(this.icon);
        }
        this.layout();
    }
    destroy(_options) {
        var _a, _b;
        super.destroy(_options);
        const wiggledestroy = (c) => {
            if (c instanceof WigglyText_1.WigglyText) {
                c.destroy(_options);
            }
        };
        (_a = this.yesButton) === null || _a === void 0 ? void 0 : _a.children.forEach(wiggledestroy);
        (_b = this.noButton) === null || _b === void 0 ? void 0 : _b.children.forEach(wiggledestroy);
    }
    get yescolor() {
        return this.noButton ? settings_1.Settings.positiveColor : settings_1.Settings.lightGrayColor;
    }
    get nocolor() {
        return settings_1.Settings.negativeColor;
    }
    yesbuttoninfo(size) {
        return {
            backgroundColor: this.yescolor,
            width: Math.max(size / (0, Global_1.px)() + 16, this.overSmallSize ? 110 : 90),
            height: this.overSmallSize ? 60 : 45
        };
    }
    nobuttoninfo(size) {
        return {
            backgroundColor: this.nocolor,
            width: Math.max(size / (0, Global_1.px)() + 16, this.overSmallSize ? 110 : 90),
            height: this.overSmallSize ? 60 : 45
        };
    }
    makeYesButton() {
        const text = new WigglyText_1.WigglyText(this.app, this.yes, blocks_1.Block.textStyle(32));
        const stay = new ui_1.ButtonContainer(this.makeBox(this.yesbuttoninfo(text.width)));
        text.anchor.set(0.5);
        text.position.set(stay.width / 2, stay.height / 2 + (0, Global_1.px)(4));
        stay.addChild(text);
        return this.background.addChild(stay);
    }
    makeNoButton() {
        const text = new WigglyText_1.WigglyText(this.app, this.no, blocks_1.Block.textStyle(32));
        const leave = new ui_1.ButtonContainer(this.makeBox(this.nobuttoninfo(text.width)));
        text.anchor.set(0.5);
        text.position.set(leave.width / 2, leave.height / 2 + (0, Global_1.px)(4));
        leave.addChild(text);
        return this.background.addChild(leave);
    }
    makeInfo() {
        const style = blocks_1.Block.textStyle(this.overSmallSize ? 32 : 28);
        style.wordWrap = true;
        style.wordWrapWidth = this.wrapwidth;
        style.align = 'center';
        style.lineHeight = (0, Global_1.px)(this.overSmallSize ? 50 : 40);
        const info = new pixi_js_1.Text(this.text, style);
        info.anchor.set(0.5);
        return this.addChild(info);
    }
    get wrapwidth() {
        return this.background.width - (0, Global_1.px)(80);
    }
    layout() {
        var _a, _b;
        super.layout();
        const centerx = this.background.width / 2;
        const h = (0, Global_1.px)(this.backgroundsettings.height);
        (_a = this.noButton) === null || _a === void 0 ? void 0 : _a.position.set(centerx - this.noButton.width - (0, Global_1.px)(10), h - this.noButton.height - (0, Global_1.px)(15));
        this.yesButton.position.set(centerx + (this.noButton ? (0, Global_1.px)(10) : -this.yesButton.width / 2), h - this.yesButton.height - (0, Global_1.px)(15));
        if (this.info) {
            this.info.position.set(this.app.screen.width / 2, this.app.screen.height / 2 + (0, Global_1.px)(40));
            this.info.style.wordWrapWidth = this.wrapwidth;
        }
        (_b = this.icon) === null || _b === void 0 ? void 0 : _b.position.set(this.background.width / 2 - (this.icon instanceof pixi_js_1.Sprite ? 0 : this.icon.width / 2), (0, Global_1.px)(30));
        // It likes getting called a second time, don't know why but works better
        super.layout();
    }
}
exports.ConfirmPopup = ConfirmPopup;
